import React, { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import links from 'appConfig/links';

import { getLanguage } from 'api/utils/languageManagment';

import { selectCountry, selectCurrentUser } from 'store/user/selectors';

import {
  OfferTypes,
  cancelFlow_cancelOfferClick,
  cancelFlow_cancelOfferScreenLoad,
} from 'services/analytics/trackers/cancelFlow';

import { useCancelSubscription, useModals } from 'hooks';
import { useSelector } from 'react-redux';

import { normalizeDateByLocale } from 'helpers/normalizeDateByLocale';

import OutlinedButton from 'components/OutlinedButton/OutlinedButton';
import ModalHeader from 'components/ModalHeader/ModalHeader';
import Modal from 'components/Modal/Modal';
import { Box, Button, Image, Input, Text } from 'components/library';

import * as S from './styled';

import feeIcon from 'assets/img/icons/fee.png';

const TermsLink: FC<PropsWithChildren> = ({ children }) => {
  const language = getLanguage();

  return (
    <a href={`${links.termsOfUse}?language=${language}`} target="_blank" rel="noreferrer">
      <Text inline medium type="large-text" className="text-underline" children={children} />
    </a>
  );
};

const BoldText = ({ content }: { content: string }) => {
  return (
    <Text inline medium type="large-text" bold>
      {content}
    </Text>
  );
};

const CancelationFeeModalContainer = () => {
  const { t } = useTranslation();
  const { closeModal } = useModals();
  const country = useSelector(selectCountry);
  const { timezone } = useSelector(selectCurrentUser);
  const [inputValue, setInputValue] = useState<string>('');
  const [cancelConfirmed, setConfirmed] = useState(false);
  const isConfirmed = inputValue.trim().toLowerCase() === 'confirm';

  const { cancellationCandidate, onCancelSubscription } = useCancelSubscription();

  const expirationDate = cancellationCandidate?.expired_date
    ? normalizeDateByLocale({
        date: cancellationCandidate.expired_date,
        country,
        timezone,
      })
    : '';

  const feeData = useMemo(() => {
    const {
      fee,
      product: { currency },
    } = cancellationCandidate!;

    return { amount: `${fee.fee_price} ${currency}` };
  }, [cancellationCandidate]);

  useEffect(() => {
    if (cancellationCandidate) {
      cancelFlow_cancelOfferScreenLoad({
        offer_type: OfferTypes.Fee_offer,
        subscription_id: cancellationCandidate.external_id,
        fee_ammount: cancellationCandidate.fee.fee_price,
      });
    }
  }, [cancellationCandidate]);

  const handleClose = () => {
    closeModal();

    if (cancellationCandidate) {
      cancelFlow_cancelOfferClick({
        action: 'close',
        subscription_id: cancellationCandidate.external_id,
        offer_type: OfferTypes.Fee_offer,
        fee_amount: cancellationCandidate?.fee.fee_price,
      });
    }
  };

  const handleGoBack = () => {
    setInputValue('');
    setConfirmed(false);
  };

  const handle1stCancel = () => {
    setConfirmed(true);

    if (cancellationCandidate) {
      cancelFlow_cancelOfferClick({
        action: 'claim_offer',
        subscription_id: cancellationCandidate.external_id,
        offer_type: OfferTypes.Fee_offer,
        fee_amount: cancellationCandidate?.fee.fee_price,
      });
    }
  };

  const handle2ndCancel = () => {
    onCancelSubscription();

    if (cancellationCandidate) {
      cancelFlow_cancelOfferClick({
        action: 'cancel',
        subscription_id: cancellationCandidate.external_id,
        offer_type: OfferTypes.Fee_offer,
        fee_amount: cancellationCandidate?.fee.fee_price,
      });
    }
  };

  return (
    <Modal isOpen fullscreen={!cancelConfirmed} withCloseButton={cancelConfirmed} onClose={handleClose}>
      {cancelConfirmed ? (
        <Box $paddingTop={60} $paddingBottom={20} $paddingX={16}>
          <Text type="h5" bold center mb={20}>
            {t('subscription.cancellation.feeOffer.title2')}
          </Text>
          <Text mb={10} center type="medium-text">
            {t('subscription.cancelFlow.commitment.confirmText')}
          </Text>
          <Input
            type="text"
            isFocus={false}
            value={inputValue}
            name="confirmation"
            placeholder={t('subscription.cancellation.feeOffer.hint')}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <Button onClick={handle2ndCancel} disabled={!isConfirmed}>
            {t('subscription.text.cancel')}
          </Button>
          <Box $paddingTop={16}>
            <OutlinedButton onClick={handleGoBack}>{t('subscription.text.goBack')}</OutlinedButton>
          </Box>
        </Box>
      ) : (
        <>
          <ModalHeader title={t('subscription.text.cancel')} onClose={handleClose} />
          <S.Container>
            <Image center maxWidth={200} src={feeIcon} alt="Fee" mb={12} />
            <Text type="h4" medium center mb={20}>
              {t('subscription.cancelFlow.commitment.payAndCancelText')}{' '}
            </Text>
            <Text mb={10} center type="large-text">
              <Trans
                i18nKey="subscription.cancellation.feeOffer.subtitle.new"
                components={[
                  <TermsLink key="0" />,
                  <BoldText key="1" content={feeData.amount} />,
                  <BoldText key="2" content={expirationDate} />,
                ]}
              />
            </Text>
            <Button onClick={handle1stCancel}>{t('subscription.text.cancel')}</Button>
            <OutlinedButton onClick={handleClose}>{t('subscription.text.keep')}</OutlinedButton>
          </S.Container>
        </>
      )}
    </Modal>
  );
};

export default CancelationFeeModalContainer;
